import React, { useState } from 'react';
import { Download, Share2, Copy, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const CARD_URL = '/holiday-invite-card.jpg';
const INVITE_MESSAGE =
  "You're invited to celebrate the season with The Women's Club of Lincoln County! 🎄 Friends, fellowship & holiday cheer — all women welcome. Join us at wclcnc.com";

export const HolidayInviteCard = () => {
  const [sharing, setSharing] = useState(false);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = CARD_URL;
    a.download = 'wclc-holiday-invite.jpg';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Holiday invite card downloaded!');
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const blob = await (await fetch(CARD_URL)).blob();
      const file = new File([blob], 'wclc-holiday-invite.jpg', { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: INVITE_MESSAGE });
      } else if (navigator.share) {
        await navigator.share({ text: INVITE_MESSAGE, url: 'https://wclcnc.com' });
      } else {
        await navigator.clipboard.writeText(`${INVITE_MESSAGE}`);
        toast.success('Sharing not supported here — invite message copied instead. Download the card to attach it!');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('Could not share — try the download button instead.');
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(INVITE_MESSAGE);
    toast.success('Invite message copied — paste it into a text or post!');
  };

  return (
    <Card className="bg-white border border-[#bd8cae]/40 shadow-md" data-testid="holiday-invite-card">
      <CardHeader>
        <CardTitle className="font-serif text-lg text-[#3c4a2a] flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#a3688f]" />
          Holiday Invite Card
        </CardTitle>
        <p className="text-sm text-[#3c4a2a]/60">
          Text it or post it — invite your friends to celebrate the season with us.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <a href={CARD_URL} target="_blank" rel="noopener noreferrer" data-testid="holiday-card-preview-link">
          <img
            src={CARD_URL}
            alt="WCLC holiday invitation card"
            className="rounded-lg border border-[#bd8cae]/30 shadow-sm w-full max-w-[260px] mx-auto hover:opacity-95 transition-opacity"
          />
        </a>
        <div className="grid grid-cols-1 gap-2">
          <Button onClick={handleShare} disabled={sharing} data-testid="holiday-card-share-btn" className="w-full bg-[#a3688f] hover:bg-[#8a567a] text-white">
            <Share2 className="w-4 h-4 mr-2" /> {sharing ? 'Opening…' : 'Share Card'}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={handleDownload} variant="outline" data-testid="holiday-card-download-btn" className="border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/15">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
            <Button onClick={handleCopy} variant="outline" data-testid="holiday-card-copy-btn" className="border-[#bd8cae]/50 text-[#3c4a2a] hover:bg-[#bd8cae]/15">
              <Copy className="w-4 h-4 mr-2" /> Copy Message
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
